import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import FormView from 'scoreboard/components/form-view'
import NewRecordOptions from './new-record-options'
import template from './fetch-new-data-form.hbs'
import { handleMissingColumnsException } from 'scoreboard/imports/new-column-name-chooser-wizard'
import Dataset from 'scoreboard/datasets/model/dataset'

/**
 * @typedef {Object} SendReloadRequestParams
 * @property {Boolean} fetchNewData
 * @property {Boolean} replaceAllRecords
 * @property {Boolean} rebuildCachedMetadata
 */

/**
 * @param dataset {Dataset}
 * @param success {Function}
 * @param error {Function}
 * @param data {Object}
 * @param params {SendReloadRequestParams}
 */
function sendReloadRequest({ dataset, success, error, data, params }) {
	const url = buildReloadUrl(dataset, params)
	const request = {
		url,
		type: 'POST',
		success,
		error
	}
	if (data) {
		request.data = data
	}
	$.ajax(request)
}

/**
 * @param dataset {Dataset}
 * @param params {SendReloadRequestParams}
 */
function buildReloadUrl(dataset, params = {}) {
	const connectionId = dataset.attributes.dataSourceMetadata.id
	const datasetId = dataset.id
	const param = $.param(params)
	return `api/datasets/${datasetId}/connections/${connectionId}/reload?${param}`
}

/**
 * This form allows users to retrieve the latest data from the
 * dataset connection source and either append it to the existing
 * data or overwrite it completely
 */
const FetchNewDataForm = Backbone.View.extend({
	tagName: 'form',

	events: {
		'click .new-record-options-container li': 'onClickNewRecordOption'
	},

	initialize: function(options = {}) {
		this.options = options
		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			// add/update records is selected by default
			allowCleanSubmission: true,
			guard: false
		})
	},

	render: function() {
		this.$el.empty().append(template())
		this.newRecordOptions = new NewRecordOptions({
			hasPrimaryKeyField: this.model?.hasPrimaryKeyField?.(),
			noConfirmation: true,
			replaceAllRecords: this.options.model.get('replaceAllRecords')
		})
		this.$('.new-record-options-container').html(this.newRecordOptions.el)
		this.newRecordOptions.render()
		this.formView.start()
		return this
	},

	onClickNewRecordOption: function(e) {
		this.newRecordOptions.handleOptionSelected(e)
	},

	submit: function() {
		this.newRecordOptions.confirmReplaceIfNeeded((replaceAllRecords, cancelled) => {
			if (cancelled) {
				// #37293 re-enable the form
				this.formView.start()
				return
			}

			this.options.model?.set('replaceAllRecords', replaceAllRecords)
			sendReloadRequest({
				dataset: this.options.model,
				success: dataset => {
					dataset = new Dataset(dataset)
					this.trigger('done', dataset)
					this.options.wizard.finish(dataset)
				},
				error: this.prepareNewColumnChooser.bind(this),
				data: this.newColumnNames ? JSON.stringify(this.newColumnNames) :
					undefined,
				params: {
					fetchNewData: true,
					replaceAllRecords: this.options.model?.get('replaceAllRecords')
				}
			})
		}, this.options.model.get('replaceAllRecords'))
	},

	buildUrl: function(replaceAllRecords) {
		const metadata = this.options.model.attributes.dataSourceMetadata
		const param = $.param({ fetchNewData: true, replaceAllRecords, rebuildCachedMetadata: true })
		return `api/datasets/${this.options.model.id}/connections/${metadata.id}/reload?${param}`
	},

	prepareNewColumnChooser: function(xhr) {
		// #37711 normally we don't like to alter the options object, but the url
		// used by the new-column-name-chooser needs to know the user's selection
		// for updating or replacing records
		this.options.url = this.buildUrl(this.options.model.get('replaceAllRecords'))
		this.options.wizard.setSubmitHandler(columnMap => {
			this.newColumnNames = columnMap
			this.submit()
		})
		if (!handleMissingColumnsException(xhr, this.options)) {
			// #37449 the updated spreadsheet does not contain enough columns to
			// remap them
			this.formView.error(xhr)
			// Fixing this requires the user to edit the file on the FTP/Google/etc.
			// server, so they could just leave this screen open, fix the problem
			// and simply click the submit button again, so we need to make sure
			// it's always enabled
			this.formView.primaryButton.enable()
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.newRecordOptions, this.formView], 'remove')
	}
})

export default FetchNewDataForm
export { sendReloadRequest }
FetchNewDataForm.__filename__ = 'datasets/fetch-new-data-form.js'
